/**
 * Movement pieces every kind shares: the walk-cycle animation (L_EB0F), the idle animation a
 * monster plays while it drops in or waits (D_1E87), falling with the one-way platform rule (D_EB3F)
 * and keeping `facing` in step with the sprite frame.
 */

import { hitRow, OFF, type Probe } from './map';
import { FALL_STEP, LINE_Y, TOP_Y, WRAP_FROM_Y, WRAP_TO_Y } from './tuning';
import type { Monster, World } from '../types';

/** L_EB0F: every second call the frame advances, wrapping within its facing group. */
export function animate(m: Monster): void {
  const a = m.ai;
  if (++a.anim < 2) return;
  a.anim = 0;
  a.frame = (a.frame + 1) & 0x1f;
  if ((a.frame & (a.faceFrames - 1)) === 0) a.frame = (a.frame - a.faceFrames) & 0x1f;
}

/** D_EE4A (Invader) and D_214A (Banebou): every second call, flip frame bit 0. */
export function toggleFrame(m: Monster): void {
  const a = m.ai;
  if (++a.anim < 2) return;
  a.anim = 0;
  a.frame ^= 1;
}

/** D_EFEA (Pulpul): every second call, the next of frames 0..3. */
export function cycleFour(m: Monster): void {
  const a = m.ai;
  if (++a.anim < 2) return;
  a.anim = 0;
  a.frame = (a.frame + 1) & 3;
}

/** D_1E87: what a monster does while it drops in or waits out its delay - animate, by kind. */
export function idleAnim(m: Monster): void {
  if (m.kind === 'banebou' || m.kind === 'invader') toggleFrame(m);
  else if (m.kind === 'pulpul') cycleFour(m);
  else animate(m);
}

/** The sprite faces left when its frame is in the second group (the C64 has only the frame). */
export function syncFacing(m: Monster): void {
  m.facing = m.ai.faceFrames > 0 && m.ai.frame >= m.ai.faceFrames ? -1 : 1;
}

/**
 * Falling's landing rule (D_EB3F from $EB5F; the same in D_273D and the jump descent): only on an
 * 8-px line; if a tile beside the lower body is solid it passes through, else a solid tile under
 * the feet lands it. `p` is the probe taken before this tick's move (one row up).
 */
export function landsOnLine(world: World, p: Probe, y: number): boolean {
  if (y < TOP_Y || ((y - LINE_Y) & 7) !== 0) return false;
  return !hitRow(world, p, OFF.floor) && hitRow(world, p, OFF.below);
}

/**
 * One falling step, 2 px down; through Y $F5 it wraps to $15. Returns true when it landed.
 * D_EB3F (walkers), D_273D (an escaped monster).
 */
export function fallStep(world: World, m: Monster, p: Probe): boolean {
  m.y = (m.y + FALL_STEP) & 0xff;
  if (m.y === WRAP_FROM_Y) {
    m.y = WRAP_TO_Y;
    return false;
  }
  return landsOnLine(world, p, m.y);
}
